import React from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { OktonAlert, OktonInput, OktonLoadingState } from "@okton/platform-ui";
import { AiBudgetError, AiBudgetState, requestAiBudget } from "./aiBudgetApi";

interface Props {
  accessToken?: string | null;
}
const money = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const usageMoney = (micros: number) =>
  (micros / 1000000).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
function parseCents(text: string): number | null {
  const match = /^(0|[1-9][0-9]{0,6})(?:[.,]([0-9]{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  const cents =
    Number(match[1]) * 100 + Number((match[2] || "").padEnd(2, "0"));
  return cents <= 100000000 ? cents : null;
}

export const AiBudgetPanel: React.FC<Props> = ({ accessToken }) => {
  const [state, setState] = React.useState<AiBudgetState | null>(null);
  const [value, setValue] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [requiresReload, setRequiresReload] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const controllerRef = React.useRef<AbortController | null>(null);
  const savingRef = React.useRef(false);
  const tokenRef = React.useRef(accessToken);
  tokenRef.current = accessToken;
  const hasSession = Boolean(accessToken);
  const apply = (saved: AiBudgetState) => {
    setState(saved);
    setValue((saved.limitCents / 100).toFixed(2).replace(".", ","));
  };
  React.useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    savingRef.current = false;
    setSaving(false);
    setLoading(true);
    setState(null);
    setError("");
    setMessage("");
    setRequiresReload(false);
    if (!hasSession) {
      setError("Sessão indisponível. Entre novamente no SSO Admin.");
      setLoading(false);
      return () => controller.abort();
    }
    requestAiBudget(tokenRef.current!, controller.signal)
      .then((saved) => {
        if (!controller.signal.aborted) apply(saved);
      })
      .catch((failure) => {
        if (!controller.signal.aborted)
          setError(
            failure instanceof Error
              ? failure.message
              : "Erro ao carregar orçamento.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [hasSession, reload]);

  async function save(): Promise<void> {
    const controller = controllerRef.current;
    if (
      !state ||
      !controller ||
      controller.signal.aborted ||
      savingRef.current ||
      requiresReload ||
      !tokenRef.current
    )
      return;
    const limitCents = parseCents(value);
    setMessage("");
    if (limitCents === null) {
      setError(
        "Informe um valor de 0 a 1000000, com até duas casas decimais e sem separador de milhar.",
      );
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const saved = await requestAiBudget(tokenRef.current, controller.signal, {
        limitCents,
        revision: state.revision,
      });
      if (!controller.signal.aborted) {
        apply(saved);
        setMessage("Limite salvo.");
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(
          failure instanceof Error ? failure.message : "Erro ao salvar limite.",
        );
        if (!(failure instanceof AiBudgetError) || failure.status !== 422)
          setRequiresReload(true);
      }
    } finally {
      if (controllerRef.current === controller && !controller.signal.aborted) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }
  return (
    <Box borderWidth="1px" rounded="md" p="5" mb="6">
      <Heading size="sm" mb="2">
        Orçamento da IA Okton
      </Heading>
      <Text fontSize="sm" mb="3">
        Teto total compartilhado por todo o piloto, incluindo mapeamento e
        consultas, sem renovação mensal.
      </Text>
      <OktonAlert status="info" mb="3">
        <Text fontSize="sm">
          {state?.meteringStatus === "ledger_ready"
            ? state.providerStatus === "ready_for_sample"
              ? "Preparado para amostra sintética. Os valores abaixo são registros da IA Okton, não a fatura da OpenAI."
              : "Provedor ainda não conectado. Os valores abaixo representam apenas chamadas registradas pela IA Okton, não a fatura da OpenAI."
            : "Medição de consumo ainda não ativada. Esta configuração será aplicada antes de liberar chamadas pagas."}
        </Text>
      </OktonAlert>
      {state?.usage && (
        <Box mb="4">
          {state.usage.halted && (
            <OktonAlert status="error" mb="3">
              <Text>
                Novas chamadas bloqueadas: foi registrado um custo acima da
                reserva. Aumentar o limite não remove essa pendência.
              </Text>
            </OktonAlert>
          )}
          <SimpleGrid columns={[1, 2, 4]} spacing="3">
            <Box>
              <Text fontSize="sm">Confirmado</Text>
              <Text fontWeight="bold">
                {usageMoney(state.usage.confirmedMicros)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm">Reservado</Text>
              <Text fontWeight="bold">
                {usageMoney(state.usage.reservedMicros)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm">Pendente de conferência</Text>
              <Text fontWeight="bold">
                {usageMoney(state.usage.pendingMicros)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm">Disponível</Text>
              <Text fontWeight="bold">
                {usageMoney(state.usage.availableMicros)}
              </Text>
            </Box>
          </SimpleGrid>
          <Text fontSize="xs" color="gray.500" mt="2">
            Valores em reais registrados com a conversão de cada chamada.
            Reservas e pendências também comprometem o limite total.
          </Text>
        </Box>
      )}
      {error && (
        <OktonAlert status="error" mb="3">
          <Text>{error}</Text>
        </OktonAlert>
      )}
      {requiresReload && (
        <OktonAlert status="warning" mb="3">
          <Text>
            Recarregue o limite antes de salvar novamente para conferir o estado
            confirmado no servidor.
          </Text>
        </OktonAlert>
      )}
      {message && (
        <OktonAlert status="success" mb="3">
          <Text>{message}</Text>
        </OktonAlert>
      )}
      {loading ? (
        <OktonLoadingState title="Carregando orçamento..." minH="120px" />
      ) : (
        state && (
          <Stack spacing="3">
            <Box maxW="sm">
              <OktonInput
                name="ai-pilot-limit"
                label="Limite total do piloto (R$)"
                inputMode="decimal"
                maxLength={11}
                value={value}
                isDisabled={saving}
                onChange={(event) => {
                  setValue(event.target.value);
                  setMessage("");
                }}
              />
            </Box>
            <Text fontSize="sm" color="gray.500">
              Alterar o limite não reinicia o histórico. Zero representa
              bloqueio, nunca uso ilimitado.
            </Text>
            <Flex>
              <Button
                size="sm"
                colorScheme="blue"
                isLoading={saving}
                isDisabled={requiresReload}
                onClick={save}
              >
                Salvar limite
              </Button>
            </Flex>
            {state.history.length > 0 && (
              <Box>
                <Heading size="xs" mb="2">
                  Últimas alterações
                </Heading>
                <Stack spacing="2">
                  {state.history.map((item) => (
                    <Text key={item.revision} fontSize="sm">
                      {new Date(item.createdAt).toLocaleString("pt-BR")} —{" "}
                      {item.subject}: {money(item.previousLimitCents)} →{" "}
                      {money(item.limitCents)}
                    </Text>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )
      )}
      {!loading && hasSession && (
        <Box mt="3">
          <Text fontSize="sm" mb="2">
            Recarregar atualiza o consumo e substitui o campo pelo limite salvo
            no servidor.
          </Text>
          <Button
            size="sm"
            onClick={() => setReload((current) => current + 1)}
            isDisabled={saving}
          >
            Recarregar limite
          </Button>
        </Box>
      )}
    </Box>
  );
};
